import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  Skeleton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  X as CloseIcon,
  Search as SearchIcon,
  ArrowLeft as ArrowLeftIcon,
} from 'lucide-mui';
import { RootState } from '../../reducers';
import AnswerDisplay from './components/AnswerDisplay';
import SearchResults from './components/SearchResults';
import {
  useSearchInput,
  useSearchPopover,
  useAssistantSearch,
  useAssistantAnswer,
  useCitationsAndThumbnails,
  useCommandSuggestions,
  useRecordingSuggestions,
} from './hooks';
import useLimits from '../plan/hooks/limits';
import useUserTags from '../../hooks/useTags';
import assistantActions from './actions';
import { FileType } from '../../types';
import { pushModal } from '../globalModals/globalModalsSlice';
import { Command } from './commands';
import { initializeRecorder } from '../recording/recordingSlice';
import { getFolderUrl } from '../../lib/routingUtils';
import { setDrawerState } from '../dashboard/dashboardSlice';
import { ContactSupportDialog } from '../support';
import RecordingSuggestions from './components/RecordingSuggestions';

interface AssistantToolbarProps {
  onFocusChange?: (isFocused: boolean) => void;
}

export const AssistantToolbar: React.FC<AssistantToolbarProps> = ({
  onFocusChange,
}) => {
  // Add state for contact support dialog
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);

  // Use the custom hooks
  const selectedVideo = useSelector(
    (state: RootState) => state.selectedVideo?.file
  );
  const location = useLocation();
  const navigate = useNavigate();
  const guardLimits = useLimits();
  const { setTag } = useUserTags();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));

  // Search results and state
  const {
    status,
    setStatus,
    results,
    setResults,
    isProcessingQuery,
    saveQueryToHistory,
    saveLastSearchResult,
    performSearch,
  } = useAssistantSearch();

  // Citations and thumbnails
  const {
    activeSource,
    setActiveSource,
    citationThumbnails,
    fileThumbnails,
    setFileThumbnails,
    featuredThumbnail,
    hideReferences,
    formatTimestamp,
    handleSourceClick: rawHandleSourceClick,
    fetchCitationThumbnail,
    fetchFileThumbnail,
    resetCitationCount,
    extractFeaturedThumbnail,
    showVideoPlayer,
    videoPlayerFile,
  } = useCitationsAndThumbnails();

  // Answer generation
  const {
    aiAnswer,
    setAiAnswer,
    isGeneratingAnswer,
    setIsGeneratingAnswer,
    lastSearchQuery,
    setLastSearchQuery,
    generateAnswerFromSearchResults,
    saveAnswer,
  } = useAssistantAnswer({ extractFeaturedThumbnail });

  // Search input handling
  const {
    query,
    setQuery,
    inputRef,
    handleInputChange: handleRawInputChange,
    handleKeyDown: handleRawKeyDown,
    handleClear: handleRawClear,
    updateQuery,
  } = useSearchInput();

  // Popover and UI state
  const {
    isPopoverOpen,
    setIsPopoverOpen,
    containerRef,
    searchBarWidth,
    isFileListExpanded,
    isSearchIconMode,
    isSearchExpanded,
    setIsSearchExpanded,
    toggleFileList,
    getShortcutText,
    screenWidth,
  } = useSearchPopover(aiAnswer, results);

  // Command suggestions
  const { suggestions } = useCommandSuggestions(query);

  // Recording suggestions
  const {
    recordingSuggestions,
    isLoading: isLoadingRecordings,
  } = useRecordingSuggestions(query);

  // State for visible command cards
  const [maxVisibleCards, setMaxVisibleCards] = useState<number>(4); // Default to 4 as a fallback

  // Get placeholder text
  const getPlaceholderText = () => {
    return 'How can I help?';
  };

  // Helper function to check if string is a URL
  const isURL = (str: string) => {
    const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return urlPattern.test(str);
  };

  // Handle opening the import modal for URLs
  const handleOpenImportModal = (url: string) => {
    dispatch(
      assistantActions.trackAssistantQuery({ query: `URL import: ${url}` })
    );
    dispatch(
      pushModal({
        type: 'import-urls-modal',
        url,
      })
    );
  };

  // Handle search submission - define this before it's used
  const handleSearch = function searchFunc() {
    if (query.trim()) {
      // Check if this is a URL
      if (isURL(query.trim())) {
        handleOpenImportModal(query.trim());
        return;
      }

      // SINGLE TRACKING POINT - Track the assistant query in PostHog
      dispatch(assistantActions.trackAssistantQuery({ query: query.trim() }));

      // Use guardLimits with a callback to show upgrade modal if needed
      guardLimits('assistant', () => {
        // First set up the loading UI immediately
        setStatus('loading');
        setIsPopoverOpen(true);
        setAiAnswer(null);
        setResults({
          data: [],
          error: null,
        });
        resetCitationCount();
        setActiveSource(null);
        // Set the last search query
        setLastSearchQuery(query.trim());

        // Record the start time to ensure minimum skeleton display time
        const startTime = Date.now();
        const MIN_LOADING_TIME = 600; // Minimum time to show loading state in milliseconds

        // Use setTimeout to ensure the loading UI is rendered before async operations begin
        setTimeout(() => {
          // Focus the input after a short delay
          if (inputRef.current) {
            inputRef.current.focus();
          }

          const doSearch = async () => {
            try {
              // Save query to history
              saveQueryToHistory(query.trim());

              // Update URL query parameter with original query
              updateQuery({ searchQuery: query.trim() });

              // Perform the search
              const searchResults = await performSearch(query.trim());

              // Extract files from search results and handle uniqueness by _id
              const filesMap = new Map<string, FileType>();
              searchResults?.forEach((result) => {
                if (result.file && !filesMap.has(result.file._id)) {
                  filesMap.set(result.file._id, result.file);
                }
              });
              const files = Array.from(filesMap.values());

              // Check if we're in a video view context using useLocation
              // Example URL structure: /library/[teamId]/[folderId]/[fileId]
              const pathSegments = location.pathname.split('/').filter(Boolean);
              const isVideoView =
                pathSegments[0] === 'library' && pathSegments.length === 4;

              // If we're in a video view, add the current video to the top of results if not already included
              if (isVideoView) {
                const currentFileId = pathSegments[3];

                // Check if the current file is already included in the results
                const hasCurrentFileInResults = files.some(
                  (file) => file._id === currentFileId
                );

                if (
                  !hasCurrentFileInResults &&
                  selectedVideo &&
                  selectedVideo._id === currentFileId
                ) {
                  console.log(
                    'Adding currently selected video to top of results:',
                    currentFileId
                  );

                  // Add the current video to the beginning of the array to prioritize it
                  files.unshift(selectedVideo);
                }
              }

              // Update the state with the search results
              setResults({
                data: files,
                error: null,
              });

              // Keep loading state active until answer generation is complete
              // Only update status to succeeded after we have both results and answer
              if (!isGeneratingAnswer) {
                // Ensure minimum loading time to prevent skeleton flashing
                const elapsedTime = Date.now() - startTime;
                if (elapsedTime < MIN_LOADING_TIME) {
                  await new Promise((resolve) =>
                    setTimeout(resolve, MIN_LOADING_TIME - elapsedTime)
                  );
                }
                setStatus('succeeded');
              }

              // Show popover with results
              setIsPopoverOpen(true);

              // Fetch thumbnails for files in the results
              await Promise.all(
                files.map(async (file) => {
                  try {
                    // Only fetch thumbnails for video files
                    const isVideoFile =
                      file.streams?.some((stream) => stream !== 'audio') ||
                      !file.streams ||
                      file.streams.length === 0;

                    if (isVideoFile) {
                      await fetchFileThumbnail(file._id);
                    }
                  } catch (error) {
                    // Log the error but don't let it affect the search flow
                    console.error('Error fetching thumbnail for file:', {
                      fileId: file._id,
                      error,
                      timestamp: new Date().toISOString(),
                    });
                  }
                })
              );

              // Set loading state before generating answer
              setIsGeneratingAnswer(true);

              // Generate answer directly from search results
              try {
                const answer = await generateAnswerFromSearchResults(
                  query.trim(),
                  searchResults
                );

                setAiAnswer(answer);

                // Save the answer
                saveAnswer(query.trim(), answer);

                // Extract featured thumbnail from the answer
                extractFeaturedThumbnail(answer, files);

                // Save the search results
                saveLastSearchResult(query.trim(), answer, files);

                // Finally set status to succeeded after everything is complete
                // Ensure minimum loading time to prevent skeleton flashing
                const elapsedTime = Date.now() - startTime;
                if (elapsedTime < MIN_LOADING_TIME) {
                  await new Promise((resolve) =>
                    setTimeout(resolve, MIN_LOADING_TIME - elapsedTime)
                  );
                }
                setStatus('succeeded');
              } catch (answerError) {
                console.error('Error generating AI answer:', answerError);

                // Set a fallback answer to ensure something is displayed
                const fallbackAnswer = `I found some information about "${query.trim()}" but encountered an issue generating a complete answer. You can explore the search results below to find relevant information.`;

                setAiAnswer(fallbackAnswer);
                saveAnswer(query.trim(), fallbackAnswer);
                setStatus('succeeded');
              } finally {
                // Ensure we clear the generating answer state
                setIsGeneratingAnswer(false);
              }
            } catch (error) {
              console.error('Assistant search error:', {
                error,
                query: query.trim(),
                timestamp: new Date().toISOString(),
                errorMessage:
                  error instanceof Error ? error.message : 'Unknown error',
              });

              setResults({
                data: [],
                error: error instanceof Error ? error.message : 'Search failed',
              });
              setStatus('failed');
              setIsGeneratingAnswer(false);
            }
          };

          // Execute the search
          doSearch();
        }, 0);
      });
    }
  };

  // Adapter functions to work with the hooks API
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleRawInputChange(e);

    // If user is typing, set loading state for any future queries
    const newQuery = e.target.value;
    if (newQuery.length > 0 && status === 'succeeded') {
      // Don't clear results or answers yet, but prepare UI for a potential new search
      setStatus('idle');
    }

    // If query is empty and we have results, show them
    if (
      newQuery.length === 0 &&
      (aiAnswer || (results.data && results.data.length > 0))
    ) {
      setIsPopoverOpen(true);
    }
    // If query is empty and no results, close popover
    else if (
      newQuery.length === 0 &&
      !aiAnswer &&
      (!results.data || results.data.length === 0)
    ) {
      setIsPopoverOpen(false);
    }

    // If query has content, always show popover
    if (newQuery.length > 0) {
      setIsPopoverOpen(true);
    }
  };

  // Determine if the component is focused for styling purposes
  const isFocused =
    isPopoverOpen ||
    query.length > 0 ||
    (document.activeElement === inputRef.current && screenWidth < 600);

  // Handle command action
  const handleCommandAction = (command: Command) => {
    // Track the command usage
    dispatch(
      assistantActions.trackAssistantQuery({
        query: `${command.key}: ${command.command}`,
      })
    );

    // Track the command action click
    dispatch(
      assistantActions.trackAssistantAction({
        actionType: command.action,
        actionName: command.key,
        query: query.trim() || `${command.key}: ${command.command}`,
      })
    );

    // Get path info
    const { pathname } = location;
    const pathSegments = pathname.split('/').filter(Boolean);
    const isLibraryContext =
      pathSegments[0] === 'library' && pathSegments.length >= 3;
    const contextTeamId = isLibraryContext ? pathSegments[1] : null;
    const contextFolderId = isLibraryContext ? pathSegments[2] : null;

    // Close the assistant panel
    setIsPopoverOpen(false);

    // Notify parent component that assistant is no longer focused
    onFocusChange?.(false);

    // Handle different actions
    switch (command.action) {
      case 'import-from-url':
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          })
        );
        break;
      case 'record-audio':
        if (isLibraryContext && contextTeamId && contextFolderId) {
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: false, webCam: false },
          };
          dispatch(
            initializeRecorder({
              teamId: contextTeamId,
              folderId: contextFolderId,
              settings,
            })
          );
        } else {
          // Redirect to library view
          navigate('/library');
        }
        break;
      case 'navigate-to-folder':
        // Handle folder navigation command with folder ID
        if (isLibraryContext && contextTeamId && command.folderId) {
          // Navigate directly to the folder using the stored folder ID
          navigate(
            getFolderUrl({
              teamId: contextTeamId,
              folderId: command.folderId,
              withBasePath: false,
            })
          );

          // Close the drawer on mobile
          if (isMobileViewport) {
            dispatch(setDrawerState('closed'));
          }
        } else {
          // Not in library context, redirect to library
          navigate('/library');
        }
        break;
      case 'record-screen':
        // Check if this is meant to be a meeting
        if (
          command.key === 'instant-meeting' ||
          command.key === 'media-record-meeting'
        ) {
          navigate('/tool/p2pmeeting');
        } else if (isLibraryContext && contextTeamId && contextFolderId) {
          // Regular screen recording with context
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: true, webCam: true },
          };
          dispatch(
            initializeRecorder({
              teamId: contextTeamId,
              folderId: contextFolderId,
              settings,
            })
          );
        } else {
          // Redirect to library view
          navigate('/library');
        }
        break;
      case 'record-video':
        if (isLibraryContext && contextTeamId && contextFolderId) {
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: false, webCam: true },
          };
          dispatch(
            initializeRecorder({
              teamId: contextTeamId,
              folderId: contextFolderId,
              settings,
            })
          );
        } else {
          // Redirect to library view
          navigate('/library');
        }
        break;
      case 'upload':
        // Use existing actions for uploads
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          })
        );
        break;

      case 'join-meeting':
        navigate('/settings/meetingbot');
        break;
      case 'calendar-connect':
        navigate('/settings/meetingbot');
        break;
      case 'add-team-member':
        dispatch(
          pushModal({
            type: 'invite-by-link-modal',
          })
        );
        break;
      case 'integrations':
        navigate('/settings/integrations');
        break;
      case 'mute-settings':
        navigate('/settings/recorder');
        break;
      case 'rating-settings':
        navigate('/settings/user');
        break;
      case 'recording-history':
        navigate('/library');
        break;
      // Add all settings navigation handlers
      case 'user-settings':
        navigate('/settings/user');
        break;
      case 'team-settings':
        navigate('/settings/team');
        break;
      case 'display-settings':
        navigate('/settings/display');
        break;
      case 'ai-settings':
        navigate('/settings/ai');
        break;
      case 'usage-settings':
        navigate('/settings/usage');
        break;
      case 'recorder-settings':
        navigate('/settings/recorder');
        break;
      case 'billing-settings':
        navigate('/settings/billing');
        break;
      case 'plugin-settings':
        navigate('/settings/plugin');
        break;
      case 'integrations-settings':
        navigate('/settings/integrations');
        break;
      case 'meetingbot-settings':
        navigate('/settings/meetingbot');
        break;
      case 'recovery-settings':
        navigate('/settings/recovery');
        break;
      case 'contact-support':
        setSupportDialogOpen(true);
        break;
      // Add more action handlers here
      default:
        // Default to import-from-url for commands that don't have specific implementations yet
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          })
        );
        break;
    }

    // Clear the input
    setQuery('');
  };

  // Handle key press events with our specific search
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Check if input is a URL before passing to original handler
    if (e.key === 'Enter' && query.trim() && isURL(query.trim())) {
      e.preventDefault();
      handleOpenImportModal(query.trim());
      return;
    }

    // Process the first suggestion card if Enter is pressed and there are suggestions
    if (e.key === 'Enter' && suggestions.length > 0 && isPopoverOpen) {
      e.preventDefault();
      handleCommandAction(suggestions[0]);
      return;
    }

    // Otherwise use original handler
    handleRawKeyDown(e, handleSearch, () => setIsPopoverOpen(false));
  };

  // Determine if there's content to display (results, answer, or processing state)
  const isLoading = status === 'loading';
  const isProcessing = isProcessingQuery || isGeneratingAnswer;
  const hasContent =
    results.data.length > 0 || aiAnswer !== null || status === 'failed';
  const shouldShowContent = hasContent || isLoading || isProcessing;

  // Determine if the popover has any actual content to show
  const hasPopoverContentToShow =
    shouldShowContent ||
    suggestions.length > 0 ||
    recordingSuggestions.length > 0 ||
    isLoadingRecordings;

  // Determine if the popover should be visible
  const shouldShowPopover = isPopoverOpen && hasPopoverContentToShow;

  // Should we round the search bar's bottom corners - only change if there's something to show
  const shouldRoundBottomCorners = !hasPopoverContentToShow || !isPopoverOpen;

  // Handle input focus with our specific state
  const handleInputFocus = () => {
    // Focus the input
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Only open popover if there's actual content to show
    if (hasPopoverContentToShow) {
      setIsPopoverOpen(true);
    } else {
      // If no content to show, keep popover closed
      setIsPopoverOpen(false);
    }

    // If in icon mode, expand the search bar
    if (isSearchIconMode) {
      setIsSearchExpanded(true);
    }

    // If we have a lastSearchQuery but no current query, make sure it's displayed
    if (lastSearchQuery && !query.trim() && aiAnswer) {
      setLastSearchQuery(lastSearchQuery);
    }

    // Always open popover on small viewports if there's content to show
    if (screenWidth < 600 && hasPopoverContentToShow) {
      setIsPopoverOpen(true);
    }

    // Notify parent component that assistant is focused
    onFocusChange?.(true);
  };

  // Handle mouse down in popover to prevent blur and focus issues
  const handlePopoverMouseDown = (e: React.MouseEvent) => {
    // Stop propagation to prevent focus issues
    e.stopPropagation();
  };

  // Close the popover
  const handleClose = () => {
    // Don't clear the AI answer or results, just close the popover UI
    setIsPopoverOpen(false);

    // Notify parent component that assistant is no longer focused
    onFocusChange?.(false);
  };

  // Handle clear with our specific state
  const handleClear = (e: React.MouseEvent) => {
    handleRawClear(e, () => {
      // Only clear the query, but keep the popover open if there are results to show
      handleRawInputChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>);

      // If we have a lastSearchQuery and aiAnswer, make sure it's displayed
      if (lastSearchQuery && aiAnswer) {
        setLastSearchQuery(lastSearchQuery);
      }

      // On small viewports, always keep the assistant visible and focused
      if (screenWidth < 600) {
        // Keep popover open on small screens
        setIsPopoverOpen(true);
        // Keep the input focused
        if (inputRef.current) {
          inputRef.current.focus();
        }
        return;
      }

      // For larger viewports, follow the original behavior
      if (!hasContent) {
        setIsPopoverOpen(false);
        // Only notify parent that focus is lost if there's no content to display
        onFocusChange?.(false);
      }
    });
  };

  // Add focus detection for small viewports
  useEffect(() => {
    const handleFocusChange = () => {
      if (screenWidth < 600 && document.activeElement === inputRef.current) {
        // Ensure popover is open when input is focused on small viewports
        setIsPopoverOpen(true);
      }
    };

    // Listen for focus events
    if (inputRef.current) {
      inputRef.current.addEventListener('focus', handleFocusChange);
    }

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('focus', handleFocusChange);
      }
    };
  }, [screenWidth, setIsPopoverOpen]);

  // Handle back button press (for all viewports)
  const handleBackButtonPress = (
    e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>
  ) => {
    // Prevent default behavior and stop propagation to prevent jumping
    e.preventDefault();
    e.stopPropagation();

    // Close the popover
    setIsPopoverOpen(false);

    // Update the query state directly to empty string
    handleRawInputChange({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);

    // If we have a lastSearchQuery and aiAnswer, make sure it's displayed
    if (lastSearchQuery && aiAnswer) {
      setLastSearchQuery(lastSearchQuery);
    }

    // Notify parent component that assistant is no longer focused
    onFocusChange?.(false);
  };

  // Handle clearing the search history
  const handleClearHistory = useCallback(() => {
    // Clear the stored answer and results
    setTag({ key: 'assistant_last_answer', value: '' });
    setTag({ key: 'assistant_last_results', value: '' });

    // Clear the UI state
    setAiAnswer(null);
    setLastSearchQuery(null);
    setResults({ data: [], error: null });
    setStatus('idle');

    // Close the popover if it's open
    setIsPopoverOpen(false);

    // Clear the input field
    if (inputRef.current) {
      inputRef.current.value = '';
      handleRawInputChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [
    setTag,
    setAiAnswer,
    setLastSearchQuery,
    setResults,
    setStatus,
    setIsPopoverOpen,
    handleRawInputChange,
  ]);

  // Function to handle expanding the search bar from icon mode
  const handleSearchIconClick = useCallback(() => {
    if (isSearchIconMode && !isSearchExpanded) {
      setIsSearchExpanded(true);
      // Focus the input after expanding
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [isSearchIconMode, isSearchExpanded, setIsSearchExpanded]);

  // Use for collapsing when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isSearchIconMode &&
        isSearchExpanded &&
        !query.trim()
      ) {
        setIsSearchExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchIconMode, isSearchExpanded, query, setIsSearchExpanded]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: { xs: '100px', sm: '300px' },
        maxWidth: '100%',
        mx: 0,
        ml: { xs: '8px', sm: '8px', md: '16px' },
        mr: { xs: isFocused ? 0 : '2px', sm: 0 },
        flexGrow: 1,
        display: 'flex',
        zIndex: isFocused ? 1200 : 1150, // Increase z-index when focused
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        overflowAnchor: 'none', // Prevent scroll anchoring
      }}
      ref={containerRef}
      onClick={(e) => e.stopPropagation()} // Prevent click propagation
    >
      {/* Search Icon Button (when in icon mode and not expanded) */}
      {isSearchIconMode && !isSearchExpanded ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '20px',
            backgroundColor: 'background.paper',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
          onClick={handleSearchIconClick}
        >
          <SearchIcon width={20} height={20} color="action" />
        </Box>
      ) : (
        <TextField
          placeholder={getPlaceholderText()}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          inputRef={inputRef}
          variant="outlined"
          fullWidth
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleInputFocus();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  // Enhanced mobile visibility
                  ml: { xs: 0.75, sm: 0.5 },
                  minWidth: { xs: 30, sm: 30 },
                  p: { xs: 0.25, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    color: 'rgba(0, 0, 0, 0.38)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: 18,
                    cursor: isFocused || isPopoverOpen ? 'pointer' : 'default',
                    position: 'relative',
                    overflow: 'hidden',
                    minWidth: { xs: 24, sm: 18 },
                    mr: { xs: 0.5, sm: 0 },
                  }}
                  onClick={
                    isFocused || isPopoverOpen
                      ? handleBackButtonPress
                      : undefined
                  }
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      opacity: isFocused || isPopoverOpen ? 1 : 0,
                      transform:
                        isFocused || isPopoverOpen ? 'scale(1)' : 'scale(0.8)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      visibility:
                        isFocused || isPopoverOpen ? 'visible' : 'hidden',
                    }}
                  >
                    <ArrowLeftIcon width={18} height={18} />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      opacity: isFocused || isPopoverOpen ? 0 : 1,
                      transform:
                        isFocused || isPopoverOpen ? 'scale(0.8)' : 'scale(1)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      visibility:
                        isFocused || isPopoverOpen ? 'hidden' : 'visible',
                    }}
                  >
                    <SearchIcon width={18} height={18} />
                  </Box>
                </Box>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{
                  mr: { xs: 0.5, sm: 0.75 },
                  my: 0,
                  minWidth: 'auto',
                  display: 'flex',
                  gap: 0.75,
                }}
              >
                {/* Keyboard shortcut text */}
                {!query && !isPopoverOpen && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.38)',
                      mr: 1,
                      userSelect: 'none',
                    }}
                  >
                    {getShortcutText()}
                  </Typography>
                )}
                {/* Show clear button ONLY when there's query text */}
                {query && (
                  <Box
                    component="button"
                    type="button"
                    onClick={handleClear}
                    sx={{
                      cursor: isLoading || isProcessing ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      width: '22px',
                      height: '22px',
                      opacity: 1,
                      borderRadius: '50%',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: 'scale(1)',
                      '&:hover': {
                        opacity: isLoading || isProcessing ? 1 : 1,
                        backgroundColor:
                          isLoading || isProcessing ? 'transparent' : 'white',
                        transform:
                          isLoading || isProcessing ? 'scale(1)' : 'scale(1.1)',
                      },
                      '&:active': {
                        transform:
                          isLoading || isProcessing
                            ? 'scale(1)'
                            : 'scale(0.95)',
                      },
                    }}
                  >
                    {isLoading || isProcessing ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <CloseIcon width={18} height={18} />
                    )}
                  </Box>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            position: 'relative',
            zIndex: 1125, // Above any other elements
            ...(isFocused && {
              width: '100%',
            }),
            '& .MuiOutlinedInput-root': {
              height: '40px',
              backgroundColor: 'background.paper',
              borderRadius: shouldRoundBottomCorners ? '20px' : '20px 20px 0 0',
              ...(isFocused && {
                borderRadius: {
                  xs: shouldRoundBottomCorners ? '16px' : '16px 16px 0 0',
                  sm: shouldRoundBottomCorners ? '20px' : '20px 20px 0 0',
                },
              }),
              '& fieldset': {
                border: 'none',
                boxShadow: 'none',
              },
              display: 'flex',
              alignItems: 'center',
              paddingLeft: { xs: 0.75, sm: 0.75 }, // Restore this for non-focused state
              paddingRight: { xs: 0.5, sm: 0.5 },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                opacity: 0.9,
              },
              '&.Mui-focused': {
                opacity: 0.95,
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '0px 0px 0px 8px',
              height: '40px',
              lineHeight: '40px',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              textIndent: '0',
              display: 'flex',
              alignItems: 'center',
              '&::placeholder': {
                opacity: 0.7,
                transition: 'opacity 0.2s ease',
                '& > span': {
                  fontSize: '0.75rem',
                  color: 'rgba(0, 0, 0, 0.38)',
                  marginLeft: '4px',
                },
              },
              '&:focus::placeholder': {
                opacity: 0.4,
              },
            },
            '& .MuiInputAdornment-root': {
              height: '40px',
              maxHeight: '40px',
              marginTop: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            },
          }}
        />
      )}

      {/* Replace Popover with absolute positioned Paper - only show when there is content */}
      {shouldShowPopover && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            width: searchBarWidth
              ? `${searchBarWidth}px`
              : { xs: '90vw', sm: '400px' },
            zIndex: 1200,
            maxHeight: '70vh',
            overflow: 'auto',
            borderRadius: '0 0 20px 20px',
            bgcolor: 'background.paper',
            border: 'none',
            marginTop: '-1px',
            opacity: 0,
            transform: 'translateY(-8px)',
            animation: 'slideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards',
            '@keyframes slideIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(-8px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
          onMouseDown={handlePopoverMouseDown}
        >
          <Box sx={{ p: 0.75 }} onMouseDown={handlePopoverMouseDown}>
            {/* Command Suggestions - show as soon as there are matches */}
            {suggestions.length > 0 && (
              <Box
                sx={{
                  mb: shouldShowContent ? 1 : 0,
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'nowrap',
                  p: 1,
                  backgroundColor: 'background.paper',
                  borderRadius: shouldShowContent ? '8px' : '0 0 8px 8px',
                  overflowX: 'hidden',
                }}
                ref={(el: HTMLDivElement | null) => {
                  if (el) {
                    // Use a simpler calculation with a fixed width per card that accounts for all spacing
                    // We'll use a more generous estimate to ensure more cards are visible
                    const containerWidth = el.offsetWidth;

                    // Use a smaller effective width per card (including gap) to show more cards
                    const effectiveCardWidth = 160; // Card + padding + part of the gap

                    // Calculate max cards with a bias toward showing more
                    const calculatedMaxCards = Math.max(
                      2,
                      Math.floor(containerWidth / effectiveCardWidth)
                    );

                    // Only update state if the value changes
                    if (calculatedMaxCards !== maxVisibleCards) {
                      setMaxVisibleCards(calculatedMaxCards);
                    }
                  }
                }}
              >
                {suggestions.slice(0, maxVisibleCards).map((suggestion) => (
                  <Box
                    key={suggestion.command}
                    sx={{
                      minWidth: '140px',
                      maxWidth: '180px',
                      height: 'auto',
                      p: 1.5,
                      cursor: 'pointer',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                    onClick={() => handleCommandAction(suggestion)}
                  >
                    {suggestion.icon && (
                      <suggestion.icon
                        width={18}
                        height={18}
                        color="text.secondary"
                      />
                    )}
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '1.3',
                      }}
                    >
                      {suggestion.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Recording Suggestions - show matched recordings */}
            {(recordingSuggestions.length > 0 || isLoadingRecordings) && (
              <RecordingSuggestions
                recordings={recordingSuggestions}
                isLoading={isLoadingRecordings}
              />
            )}

            {/* When loading, show skeleton loader */}
            {isLoading || isProcessing ? (
              <Box sx={{ p: 1 }}>
                {/* Main answer skeleton */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2.5,
                  }}
                >
                  {/* Thumbnail skeleton */}
                  <Skeleton
                    variant="rectangular"
                    width={240}
                    height={135}
                    sx={{
                      borderRadius: '12px',
                      display: { xs: 'none', md: 'block' },
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    }}
                  />

                  {/* Text content skeleton */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton
                      variant="text"
                      width="90%"
                      height={20}
                      sx={{
                        mb: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width="95%"
                      height={20}
                      sx={{
                        mb: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width="85%"
                      height={20}
                      sx={{
                        mb: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width="70%"
                      height={20}
                      sx={{
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      }}
                    />
                  </Box>
                </Box>

                {/* Show search results below skeleton if they're available */}
                {results.data && results.data.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <SearchResults
                      results={results}
                      isFileListExpanded={isFileListExpanded}
                      toggleFileList={toggleFileList}
                      fileThumbnails={fileThumbnails}
                      setFileThumbnails={setFileThumbnails}
                      handleClose={handleClose}
                      isLoading
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                {/* Only show the other content when not loading */}
                {status === 'failed' && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: '16px',
                      bgcolor: 'transparent',
                      color: 'error.dark',
                    }}
                  >
                    <Typography variant="body2">
                      {results.error || 'Search failed. Please try again.'}
                    </Typography>
                  </Paper>
                )}

                {/* AI Answer Section */}
                {(status === 'succeeded' || aiAnswer) && (
                  <AnswerDisplay
                    aiAnswer={aiAnswer}
                    isGeneratingAnswer={isGeneratingAnswer}
                    lastSearchQuery={lastSearchQuery}
                    results={results}
                    featuredThumbnail={featuredThumbnail}
                    activeSource={activeSource}
                    handleSourceClick={(fileId, timestamp) =>
                      rawHandleSourceClick(fileId, timestamp, results.data)
                    }
                    citationThumbnails={citationThumbnails}
                    fetchCitationThumbnail={fetchCitationThumbnail}
                    formatTimestamp={formatTimestamp}
                    hideReferences={hideReferences}
                    onClearHistory={handleClearHistory}
                    showVideoPlayer={showVideoPlayer}
                    videoPlayerFile={videoPlayerFile}
                  />
                )}

                {/* Search Results Section */}
                {status === 'succeeded' && (
                  <SearchResults
                    results={results}
                    isFileListExpanded={isFileListExpanded}
                    toggleFileList={toggleFileList}
                    fileThumbnails={fileThumbnails}
                    setFileThumbnails={setFileThumbnails}
                    handleClose={handleClose}
                  />
                )}
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {supportDialogOpen && (
        <ContactSupportDialog
          onClose={() => setSupportDialogOpen(false)}
          formSource="Assistant - Help Command"
        />
      )}
    </Box>
  );
};

export default AssistantToolbar;
